import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Button, IconButton, Pagination } from '@mui/material';
import { Add, DeleteOutline, Edit, ExpandMore, Launch } from '@mui/icons-material';

import { useProfile, withAuthorization } from '../Session';
import FormDialog from '../common/FormDialog';
import {
  SAMPLE_STATUS, canDeleteSample, canWriteProject, createCohort, createSample, deleteSample, isInternalRole,
  renameCohort, renameSample, updateProject, watchCohorts, watchProject, watchSamples, watchUsers,
} from '../../services';
// Même design que la page projet d'origine (src/components/ProjectPage).
import '../ProjectPage/styles.css';
import './styles.css';

const formatDate = ts => (ts?.toDate ? ts.toDate().toDateString() : '—');
const PAGE_SIZE = 6;
const NO_COHORT = '';

// Page d'un projet (modèle v2) : description, cohortes et échantillons ; création selon les droits.
function ProjectPageV2() {
  const { projectId } = useParams();
  const profile = useProfile();
  const [project, setProject] = useState(undefined);
  const [cohorts, setCohorts] = useState([]);
  const [samples, setSamples] = useState(null);
  const [users, setUsers] = useState({});
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);  // 'project' | 'cohort' | 'sample'
  const [toDelete, setToDelete] = useState(null);  // échantillon dont la suppression est demandée
  const [renaming, setRenaming] = useState(null);  // { kind: 'sample' | 'cohort', item } en cours de renommage
  const [expanded, setExpanded] = useState({});  // cohorte -> ouverte
  const [selected, setSelected] = useState({});  // cohorte -> id de l'échantillon affiché dans le panneau
  const [pages, setPages] = useState({});        // cohorte -> page courante

  useEffect(() => {
    if (!profile) return undefined;
    setProject(undefined);
    setSamples(null);
    const fail = err => setError(err.code === 'permission-denied'
      ? "This project doesn't exist or isn't shared with you." : err.message);
    const stops = [
      watchProject(projectId, p => { setProject(p); if (p) document.title = `Project - ${p.name}`; }, fail),
      watchCohorts(projectId, setCohorts, fail),
      watchSamples(projectId, setSamples, fail),
    ];
    // L'annuaire (noms des auteurs) n'est lisible que par les membres internes.
    if (isInternalRole(profile.role)) {
      stops.push(watchUsers(list => setUsers(Object.fromEntries(list.map(u => [u.uid, u]))), () => {}));
    }
    return () => stops.forEach(stop => stop());
  }, [projectId, profile?.uid, profile?.role]);

  const canWrite = canWriteProject(profile, project);

  // Échantillons regroupés par cohorte, cohortes vides comprises ; « Sans cohorte » en dernier s'il y en a.
  const groups = useMemo(() => {
    const list = cohorts.map(c => ({ id: c.id, label: c.label, createdAt: c.createdAt, samples: [] }));
    const byId = Object.fromEntries(list.map(g => [g.id, g]));
    const orphans = { id: NO_COHORT, label: 'No cohort', samples: [] };
    (samples || []).forEach(s => (byId[s.cohortId] || orphans).samples.push(s));
    return orphans.samples.length ? [...list, orphans] : list;
  }, [cohorts, samples]);

  if (profile === undefined || (project === undefined && !error)) {
    return <div id="project-page"><div id="header-project-page" /></div>;
  }
  if (error || !project) {
    return (
      <div id="project-page">
        <Alert severity="warning" sx={{ mt: 3, width: '94%' }}>{error || "This project doesn't exist."}</Alert>
      </div>
    );
  }

  const authorName = uid => users[uid]?.displayName || users[uid]?.email || '—';
  const cohortOptions = [{ value: NO_COHORT, label: 'No cohort' }, ...cohorts.map(c => ({ value: c.id, label: c.label }))];
  const groupKey = g => g.id || 'none';

  return (
    <div id="project-page">
      <div id="header-project-page" className="project-v2-header">
        <h1>{project.name}</h1>
        {canWrite && (
          <div className="project-v2-actions">
            <Button size="small" startIcon={<Edit />} onClick={() => setDialog('project')}>Edit</Button>
            <Button size="small" startIcon={<Add />} onClick={() => setDialog('cohort')}>Cohort</Button>
            <Button size="small" startIcon={<Add />} onClick={() => setDialog('sample')}>Sample</Button>
          </div>
        )}
      </div>
      {project.description && <p className="project-v2-description">{project.description}</p>}
      {/* Aperçu « vue externe » d'un administrateur : projet affiché, avec ce qu'un externe en verrait vraiment. */}
      {profile.preview && !(project.memberIds || []).includes(profile.uid) && (
        <Alert severity="info" sx={{ mt: 2, width: '94%' }}>
          Preview: this project isn't shared with external accounts; they only see its demo samples.
        </Alert>
      )}

      <div id="header-cohort-list-container">
        <p className="cohort-name-container">Cohort name</p>
        <p className="form-creation-date-container">Creation date</p>
        <p className="sample-number-container">Sample number</p>
      </div>
      <div id="cohort-list-container">
        <div id="cohort-list">
          {groups.length === 0 && (
            <p className="project-v2-empty">
              No cohorts yet{canWrite ? ': create one with "Cohort", then add samples.' : '.'}
            </p>
          )}
          {groups.map(g => {
            const key = groupKey(g);
            const isOpen = !!expanded[key];
            const page = pages[key] || 1;
            const shown = g.samples.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
            const current = g.samples.find(s => s.id === selected[key]);
            return (
              <Accordion className="cohort-accordion" key={key} disableGutters expanded={isOpen}>
                <AccordionSummary
                  className="cohort-accordion-summary"
                  expandIcon={<ExpandMore />}
                  onClick={() => setExpanded({ ...expanded, [key]: !isOpen })}
                  sx={isOpen ? { backgroundColor: 'rgb(247, 247, 255)', color: 'rgb(39, 72, 163)' } : {}}
                >
                  <p className="cohort-name-container">
                    {g.label}
                    {canWrite && g.id !== NO_COHORT && (
                      <IconButton size="small" className="project-v2-rename" aria-label={`Rename cohort ${g.label}`}
                        title="Rename cohort"
                        onClick={e => { e.stopPropagation(); setRenaming({ kind: 'cohort', item: g }); }}>
                        <Edit fontSize="inherit" />
                      </IconButton>
                    )}
                  </p>
                  <p className="cohort-creation-date-container">{formatDate(g.createdAt)}</p>
                  <p className="sample-number-container">{g.samples.length}</p>
                </AccordionSummary>
                <AccordionDetails className="cohort-accordion-details">
                  <div className="cohort-accordion-details-browser-container" style={{ minHeight: current ? '300px' : undefined }}>
                    <div className="sample-list-container">
                      <div className="sample-list-header">
                        <p className="sample-list-name-container">Sample name</p>
                        <p className="sample-list-last_update-container">Last update</p>
                        <p className="sample-list-size-container">Status</p>
                        <p className="sample-list-modalities-container">Modalities</p>
                        <p className="sample-list-open-viewer-icon">Viewer</p>
                      </div>
                      {shown.length === 0 && <p className="project-v2-empty">No samples in this cohort.</p>}
                      {shown.map(s => (
                        <div
                          key={s.id}
                          className="sample-list-element"
                          onClick={() => setSelected({ ...selected, [key]: s.id })}
                          style={selected[key] === s.id ? { backgroundColor: 'rgb(242, 242, 242)', cursor: 'default' } : {}}
                        >
                          <p className="sample-list-name-container">{s.name}</p>
                          <p className="sample-list-last_update-container">{formatDate(s.updatedAt || s.createdAt)}</p>
                          <p className="sample-list-size-container">{SAMPLE_STATUS[s.status] || s.status}</p>
                          <p className="sample-list-modalities-container">{s.imageCount || 0}</p>
                          <p className="sample-list-open-viewer-icon" style={{ height: '20px' }}>
                            <Link target="_blank" rel="noopener noreferrer" to={`/projects/${projectId}/${s.id}`}
                              onClick={e => e.stopPropagation()}>
                              <Launch id="open-viewer-icon" />
                            </Link>
                          </p>
                        </div>
                      ))}
                    </div>
                    {g.samples.length > PAGE_SIZE && (
                      <Pagination className="accordion-details-pagination" size="small" page={page}
                        count={Math.ceil(g.samples.length / PAGE_SIZE)}
                        onChange={(e, value) => setPages({ ...pages, [key]: value })} />
                    )}
                  </div>
                  <div className="cohort-accordion-details-sample-detail-container"
                    style={{ minWidth: current ? '250px' : '0px', height: current ? '300px' : '0px' }}>
                    {current?.thumbnailUrl && <img className="cohort-accordion-details-sample-thumbail" src={current.thumbnailUrl} alt="" />}
                    <em>Description:</em>
                    <p>{current?.description || '—'}</p>
                    <em>Created by:</em>
                    <p>{current ? authorName(current.createdBy) : ''}</p>
                    {canWrite && current && (
                      <Button size="small" startIcon={<Edit />} className="project-v2-delete"
                        onClick={() => setRenaming({ kind: 'sample', item: current })}>
                        Rename
                      </Button>
                    )}
                    {canDeleteSample(profile, project, current) && (
                      <Button size="small" color="error" startIcon={<DeleteOutline />} className="project-v2-delete"
                        onClick={() => setToDelete(current)}>
                        Delete sample
                      </Button>
                    )}
                  </div>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </div>
      </div>

      <FormDialog
        open={dialog === 'project'} title="Edit project" onClose={() => setDialog(null)}
        fields={[{ name: 'name', label: 'Name', required: true }, { name: 'description', label: 'Description', multiline: true }]}
        initialValues={{ name: project.name, description: project.description || '' }}
        onSubmit={v => updateProject(projectId, v)}
      />
      <FormDialog
        open={dialog === 'cohort'} title="New cohort" submitLabel="Create" onClose={() => setDialog(null)}
        fields={[{ name: 'label', label: 'Cohort name', required: true }, { name: 'description', label: 'Description', multiline: true }]}
        onSubmit={v => createCohort(profile, projectId, v)}
      />
      <FormDialog
        open={!!renaming} submitLabel="Rename" onClose={() => setRenaming(null)}
        title={renaming?.kind === 'cohort' ? 'Rename cohort' : 'Rename sample'}
        fields={[{ name: 'name', label: renaming?.kind === 'cohort' ? 'Cohort name' : 'Sample name or code', required: true }]}
        initialValues={{ name: renaming?.kind === 'cohort' ? renaming.item.label : renaming?.item.name }}
        onSubmit={({ name }) => (renaming.kind === 'cohort'
          ? renameCohort(projectId, renaming.item.id, name)
          : renameSample(profile, projectId, renaming.item.id, name))}
      />
      {/* Suppression définitive : nom à retaper pour confirmer. */}
      <FormDialog
        open={!!toDelete} title={`Delete "${toDelete?.name || ''}"?`} submitLabel="Delete permanently" danger
        description={`The sample, its ${toDelete?.imageCount || 0} image(s) (original files and display tiles), `
          + 'its viewer settings and its demo, if any, will be erased from the cloud for all users. '
          + 'This cannot be undone.'}
        fields={[{ name: 'confirm', label: 'Sample name, to confirm', required: true }]}
        onClose={() => setToDelete(null)}
        onSubmit={async ({ confirm }) => {
          if (confirm.trim() !== toDelete.name.trim()) throw new Error("The name you typed doesn't match the sample's name.");
          await deleteSample(profile, projectId, toDelete.id);
          setSelected(prev => Object.fromEntries(Object.entries(prev).filter(([, id]) => id !== toDelete.id)));
        }}
      />
      <FormDialog
        open={dialog === 'sample'} title="New sample" submitLabel="Create" onClose={() => setDialog(null)}
        fields={[
          { name: 'name', label: 'Sample name or code', required: true },
          { name: 'cohortId', label: 'Cohort', options: cohortOptions },
          { name: 'description', label: 'Description', multiline: true },
        ]}
        initialValues={{ cohortId: cohorts[0]?.id ?? NO_COHORT }}
        onSubmit={v => createSample(profile, projectId, v)}
      />
    </div>
  );
}

export default withAuthorization(authUser => !!authUser)(ProjectPageV2);
